import React, {useEffect, useState} from 'react'
import CustomDropdown from '../common/CustomDropdown'
import CustomInputField from '../common/CustomInputField'
import {
 fetchDistrict,
 fetchState,
 fetchSubDistrict,
 fetchTerritories,
} from 'utils/api/auth/locationService'
import {fetchOrganisationTypes} from 'utils/api/auth/userManagementService'
import {FormDataType} from 'models/ViewOrganisation'
import {fetchCommericialArea} from 'utils/api/auth/organisationService'
import {useTranslation} from 'react-i18next'
import {OrganizationTypeList} from 'models/dropdown'

type NumberOption = {
 label: string
 value: number
}

type StringOption = {
 label: string
 value: string
}

type Props = {
 formData: FormDataType
 setFormData: React.Dispatch<
  React.SetStateAction<FormDataType>
 >
 isEdit?: boolean
 selfOnboarding?: boolean
 isEditing?: boolean
}

const OrganisationFormFields = ({
 formData,
 setFormData,
 isEdit = false,
 selfOnboarding = false,
 isEditing = false,
}: Props) => {
 const {t} = useTranslation()
 const [stateOptions, setStateOptions] = useState<
  NumberOption[]
 >([])
 const [districtOptions, setDistrictOptions] = useState<
  NumberOption[]
 >([])
 const [subDistrictOptions, setSubDistrictOptions] =
  useState<NumberOption[]>([])
 const [territoryOptions, setTerritoryOptions] = useState<
  NumberOption[]
 >([])
 const [commercialAreaOptions, setCommercialAreaOptions] =
  useState<NumberOption[]>([])
 const [typeOptions, setTypeOptions] = useState<
  StringOption[]
 >([])

 const handleChange = <K extends keyof FormDataType>(
  key: K,
  value: FormDataType[K]
 ) => {
  setFormData((prev) => ({
   ...prev,
   [key]: value,
  }))
 }
 const handleTypeChange = (val: StringOption) => {
  setFormData((prev) => ({
   ...prev,
   type: val.value,
   type_name: val.label,
  }))
 }
 const handleStateChange = (val: NumberOption) => {
  setFormData((prev) => ({
   ...prev,
   state: val.value,
   state_name: val.label,
   district: null,
   district_name: '',
   subDistrict: null,
   subDistrict_name: '',
   territory: null,
   territory_name: '',
   commercial_area: null,
   commercial_area_name: '',
  }))

  setDistrictOptions([])
  setSubDistrictOptions([])
  setTerritoryOptions([])
  setCommercialAreaOptions([])
 }

 const handleDistrictChange = (val: NumberOption) => {
  setFormData((prev) => ({
   ...prev,
   district: val.value,
   district_name: val.label,

   subDistrict: null,
   subDistrict_name: '',

   territory: null,
   territory_name: '',

   commercial_area: null,
   commercial_area_name: '',
  }))

  setSubDistrictOptions([])
  setTerritoryOptions([])
  setCommercialAreaOptions([])
 }

 const handleSubDistrictChange = (val: NumberOption) => {
  setFormData((prev) => ({
   ...prev,
   subDistrict: val.value,
   subDistrict_name: val.label,

   commercial_area: null,
   commercial_area_name: '',
  }))

  setCommercialAreaOptions([])
 }
 const handleTerritoryChange = (val: NumberOption) => {
  setFormData((prev) => ({
   ...prev,
   territory: val.value,
   territory_name: val.label,
  }))
 }

 const handleCommercialAreaChange = (val: NumberOption) => {
  setFormData((prev) => ({
   ...prev,
   commercial_area: val.value,
   commercial_area_name: val.label,
  }))
 }

 useEffect(() => {
  const loadStates = async () => {
   const res = await fetchState()

   const formattedStates: NumberOption[] = (res ?? []).map(
    (item: {id: number; name: string}) => ({
     label: item.name,
     value: item.id,
    })
   )

   setStateOptions(formattedStates)
  }

  void loadStates()
 }, [])

 useEffect(() => {
  if (!formData.state || stateOptions.length === 0) return

  const exists = stateOptions.find(
   (opt) => opt.value === formData.state
  )

  if (!exists) {
   setFormData((prev) => ({
    ...prev,
    state: null,
   }))
  }
 }, [stateOptions])

 useEffect(() => {
  const loadTypes = async () => {
   const res = await fetchOrganisationTypes({
    distributor_needed: true,
   })

   const types = (res ?? []) as OrganizationTypeList[]

   const formattedTypes: StringOption[] = types.map(
    (item) => ({
     label: item.display_name,
     value: item.key,
    })
   )

   setTypeOptions(formattedTypes)
  }

  void loadTypes()
 }, [])

 useEffect(() => {
  if (!typeOptions.length || !formData.type) {
   return
  }

  const matched = typeOptions.find(
   (opt) =>
    opt.value === formData.type ||
    opt.value.toUpperCase() ===
     formData.type.toUpperCase() ||
    opt.label.toUpperCase() === formData.type.toUpperCase()
  )

  if (
   matched &&
   (matched.value !== formData.type ||
    matched.label !== formData.type_name)
  ) {
   setFormData((prev) => ({
    ...prev,
    type: matched.value,
    type_name: matched.label,
   }))
  }
 }, [typeOptions, formData.type, formData.type_name])

 useEffect(() => {
  if (!formData.state) return

  const loadDistricts = async () => {
   const res = await fetchDistrict(formData.state)

   const formatted = (res || []).map(
    (item: {name: string; id: number}) => ({
     label: item.name,
     value: item.id,
    })
   )

   setDistrictOptions(formatted)
  }

  void loadDistricts()
 }, [formData.state])

 useEffect(() => {
  if (!formData.district) return

  const loadData = async () => {
   const sub = await fetchSubDistrict(formData.district)
   setSubDistrictOptions(
    (sub || []).map((item: {name: string; id: number}) => ({
     label: item.name,
     value: item.id,
    }))
   )

   const terr = await fetchTerritories(formData.district)
   setTerritoryOptions(
    (terr || []).map(
     (item: {name: string; id: number}) => ({
      label: item.name,
      value: item.id,
     })
    )
   )
  }

  void loadData()
 }, [formData.district])

 useEffect(() => {
  if (!formData.district || !formData.subDistrict) return

  const loadCommercial = async () => {
   const res = await fetchCommericialArea(
    formData.district,
    formData.subDistrict
   )

   setCommercialAreaOptions(
    (res || []).map((item: {name: string; id: number}) => ({
     label: item.name,
     value: item.id,
    }))
   )
  }

  void loadCommercial()
 }, [formData.subDistrict])

 return (
  <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5'>
   <CustomInputField
    label={t('organisation.organisationForm.name')}
    required
    placeholder={t(
     'organisation.organisationForm.namePlaceholder'
    )}
    value={formData.name}
    onChange={(e) => handleChange('name', e.target.value)}
    disabled={isEdit && !isEditing}
   />
   <CustomDropdown<StringOption>
    label={t('organisation.organisationForm.type')}
    required
    placeholder={t(
     'organisation.organisationForm.typePlaceholder'
    )}
    value={
     typeOptions.find(
      (opt) => opt.value === formData.type
     ) ?? null
    }
    options={typeOptions}
    onChange={(val) => handleTypeChange(val)}
    disabled={isEdit}
   />

   <CustomInputField
    required={selfOnboarding}
    label={t('organisation.organisationForm.pan')}
    placeholder={t(
     'organisation.organisationForm.panPlaceholder'
    )}
    value={formData.pan_number}
    onChange={(e) =>
     handleChange('pan_number', e.target.value)
    }
    disabled={isEdit && !isEditing}
   />
   <CustomInputField
    label={t('organisation.organisationForm.gstinNumber')}
    placeholder={t(
     'organisation.organisationForm.gstinPlaceholder'
    )}
    value={formData.gst_number}
    onChange={(e) =>
     handleChange('gst_number', e.target.value)
    }
    disabled={isEdit && !isEditing}
   />

   <CustomDropdown<NumberOption>
    label={t('organisation.organisationForm.state')}
    required
    placeholder={t(
     'organisation.organisationForm.statePlaceholder'
    )}
    value={
     stateOptions.find(
      (opt) => opt.value === formData.state
     ) ?? null
    }
    options={stateOptions}
    onChange={handleStateChange}
    disabled={isEdit}
   />
   <CustomDropdown<NumberOption>
    label={t('organisation.organisationForm.district')}
    value={
     districtOptions.find(
      (opt) => opt.value === formData.district
     ) ?? null
    }
    options={districtOptions}
    onChange={handleDistrictChange}
    disabled={!formData.state || isEdit}
   />
   <CustomDropdown<NumberOption>
    label={t('organisation.organisationForm.subDistrict')}
    value={
     subDistrictOptions.find(
      (opt) => opt.value === formData.subDistrict
     ) ?? null
    }
    options={subDistrictOptions}
    onChange={handleSubDistrictChange}
    disabled={!formData.district || isEdit}
   />

   <CustomDropdown<NumberOption>
    label={t('organisation.organisationForm.territory')}
    placeholder={t(
     'organisation.organisationForm.territoryPlaceholder'
    )}
    value={
     territoryOptions.find(
      (opt) => opt.value === formData.territory
     ) ?? null
    }
    options={territoryOptions}
    onChange={handleTerritoryChange}
    disabled={!formData.district || isEdit}
   />
   <CustomDropdown<NumberOption>
    label={t(
     'organisation.organisationForm.commercialArea'
    )}
    placeholder={t(
     'organisation.organisationForm.commercialAreaPlaceholder'
    )}
    value={
     commercialAreaOptions.find(
      (opt) => opt.value === formData.commercial_area
     ) ?? null
    }
    options={commercialAreaOptions}
    onChange={handleCommercialAreaChange}
    disabled={!formData.subDistrict || isEdit}
   />

   <CustomInputField
    label={t('organisation.organisationForm.pincode')}
    placeholder={t(
     'organisation.organisationForm.pincodePlaceholder'
    )}
    value={formData.pincode}
    onChange={(e) =>
     handleChange('pincode', e.target.value)
    }
    disabled={isEdit && !isEditing}
   />

   <CustomInputField
    label={t('organisation.organisationForm.address')}
    placeholder={t(
     'organisation.organisationForm.addressPlaceholder'
    )}
    value={formData.full_address}
    onChange={(e) =>
     handleChange('full_address', e.target.value)
    }
    disabled={isEdit && !isEditing}
   />
  </div>
 )
}

export default OrganisationFormFields
